import type { Attachment, MessageDetail } from '../lib/api';
import { formatBytes, fullTime } from '../lib/format';
import { MessageBody } from './MessageBody';
import { ReplyBox } from './ReplyBox';

interface ReaderPaneProps {
  readonly message: MessageDetail | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly sendingEnabled: boolean;
  readonly sending: boolean;
  readonly sendError: string | null;
  readonly inboxAddress: string | null;
  readonly onDelete: (id: string) => void;
  readonly onDownload: (attachment: Attachment) => void;
  readonly onReply: (text: string) => Promise<boolean>;
  readonly onBack: () => void;
}

export function ReaderPane({
  message,
  loading,
  error,
  sendingEnabled,
  sending,
  sendError,
  inboxAddress,
  onDelete,
  onDownload,
  onReply,
  onBack,
}: ReaderPaneProps) {
  if (loading) {
    return (
      <section className="pane pane--reader" aria-label="Message">
        <div className="empty">
          <div className="spinner" role="status" aria-label="Loading message" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="pane pane--reader" aria-label="Message">
        <p className="alert alert--inline" role="alert">
          {error}
        </p>
      </section>
    );
  }

  if (!message) {
    return (
      <section className="pane pane--reader" aria-label="Message">
        <div className="empty">
          <span className="empty__title">No message selected</span>
          <span className="empty__hint">Pick a message from the list to read it.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="pane pane--reader" aria-label="Message">
      <div className="reader">
        <header className="reader__head">
          <button className="btn btn--ghost back" type="button" onClick={onBack} style={{ marginBottom: 10 }}>
            ← Messages
          </button>

          <h2 className="reader__subject">{message.subject || '(no subject)'}</h2>

          <div className="reader__meta">
            <span className="reader__peer">
              {message.direction === 'out' ? 'To ' : ''}
              {message.peerName ? `${message.peerName} · ` : ''}
              {message.peerAddress}
            </span>

            <time dateTime={new Date(message.createdAt).toISOString()}>
              {fullTime(message.createdAt)}
            </time>

            <button
              className="btn btn--ghost btn--danger"
              type="button"
              onClick={() => onDelete(message.id)}
              style={{ marginLeft: 'auto' }}
            >
              Delete
            </button>
          </div>
        </header>

        <div className="reader__body">
          <MessageBody html={message.htmlBody} text={message.textBody} />
        </div>

        {message.attachments.length > 0 ? (
          <div className="attachments">
            {message.attachments.map((attachment) => (
              <button
                key={attachment.id}
                className="attachment"
                type="button"
                onClick={() => onDownload(attachment)}
              >
                <span>{attachment.filename}</span>
                <span className="attachment__size">{formatBytes(attachment.sizeBytes)}</span>
              </button>
            ))}
          </div>
        ) : null}

        {/* Replying only makes sense on mail that came in, and only when a
            relay is configured — offering a button that always fails would be
            worse than not offering one. */}
        {sendingEnabled && message.direction === 'in' && inboxAddress ? (
          <ReplyBox
            from={inboxAddress}
            to={message.peerAddress}
            sending={sending}
            error={sendError}
            onSend={onReply}
          />
        ) : null}
      </div>
    </section>
  );
}
